import {match} from 'ts-pattern';

import type {CheckoutProfileMissing} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {CHECKOUT_PROFILE_VERSION_CONFLICT_CODES} from '../constants/CHECKOUT_PROFILE_VERSION_CONFLICT_CODES';

export type CheckoutProfileIssue = {
  code: string;
  message: string;
  severity: 'warning' | 'error';
  tab?: CheckoutProfileMissing['tab'];
  field?: string;
} & Partial<Pick<CheckoutProfileMissing, 'saleRegime' | 'documentType' | 'paymentMethod'>>;

export function getCheckoutProfileIssueMessage(code: string, fallback: string) {
  return match(code)
    .with('PROFILE_NAME_REQUIRED', 'RULE_NAME_REQUIRED', 'CONDITION_EMPTY', () =>
      i18n.t('general.validations.fieldIsRequired')
    )
    .with('VALUE_NOT_ALLOWED', () => i18n.t('entity.checkout.validation.valueNotAllowed'))
    .with('PROFILE_NOT_FOUND', () => i18n.t('entity.checkout.validation.profileNotFound'))
    .with('PROFILE_NAME_NOT_UNIQUE', () =>
      i18n.t('entity.checkout.validation.profileNameNotUnique')
    )
    .with('PROFILE_FIELD_IMMUTABLE', () =>
      i18n.t('entity.checkout.validation.profileFieldImmutable')
    )
    .with('PROFILE_USED_BY_ACTIVE_RULE', () =>
      i18n.t('entity.checkout.validation.profileUsedByActiveRule')
    )
    .with('FIELD_NOT_ALLOWED_FOR_INTERNAL', () =>
      i18n.t('entity.checkout.validation.internalFieldNotAllowed')
    )
    .with('RULE_NOT_FOUND', () => i18n.t('entity.automationRule.detail.notFound'))
    .with('RULE_CONFLICT', () => i18n.t('entity.checkout.labels.ruleConflicts'))
    .with('RULE_PROFILE_BRANCH_MISMATCH', () =>
      i18n.t('entity.checkout.validation.ruleProfileBranchMismatch')
    )
    .with('RULE_PROFILE_INACTIVE', () => i18n.t('entity.checkout.validation.ruleProfileInactive'))
    .with('RULE_SOURCE_TYPE_IMMUTABLE', () =>
      i18n.t('entity.checkout.validation.ruleSourceTypeImmutable')
    )
    .with('PRIORITY_INVALID', () => i18n.t('entity.checkout.validation.priorityInvalid'))
    .with('CONDITION_VALUE_NOT_FOUND', () =>
      i18n.t('entity.checkout.validation.conditionValueNotFound')
    )
    .with('CONDITION_NOT_FOR_SOURCE', () =>
      i18n.t('entity.checkout.validation.conditionNotForSource')
    )
    .with('VARIANT_EXCLUDES_TYPES', () => i18n.t('entity.checkout.validation.variantExcludesTypes'))
    .with('DEFAULT_NOT_IN_ALLOWED', () => i18n.t('entity.checkout.validation.defaultNotAllowed'))
    .with('DEPOSIT_METHOD_NOT_ENABLED', () =>
      i18n.t('entity.checkout.validation.depositMethodNotEnabled')
    )
    .with('DOCUMENT_ROW_NOT_APPLICABLE', () =>
      i18n.t('entity.checkout.validation.documentNotApplicable')
    )
    .with('DOCUMENT_ROW_DUPLICATE', () => i18n.t('entity.checkout.validation.documentDuplicate'))
    .with(...CHECKOUT_PROFILE_VERSION_CONFLICT_CODES, () =>
      i18n.t('entity.checkout.validation.versionConflict')
    )
    .otherwise(() => fallback);
}
